import QRCode from 'qrcode';
import { useEffect, useState } from 'react';

import { Modal, Notice } from '@/components/Modal';
import { errorMessage, today as todayString } from '@/lib/format';
import { appendCaseLine } from '@/pages/legacy/caseApi';

import { CaseSuggestPicker } from './CaseSuggestPicker';
import type { Approvals, PaperEdit } from './JournalPaper';
import { useSiteRiskItems, workLabel, type Finding, type JournalInput, type RiskItem } from './safeJournalApi';
import { SHEET_STYLES } from './sheetModel';
import { QrActions } from './QrActions';
import { GrowTextarea, SheetField, SheetHead } from './SheetParts';
import { tbmSignUrl, useTbmSignatureDelete, useTbmSignatures, useTbmSignLink, useTbmSignLinkWrite } from './tbmSignApi';

/** 중점관리 줄 → 체크리스트 한 줄. 점검내용은 감소대책(없으면 위험요인), 담당 · 예정일은 위험성평가 그대로. */
function checkOf(item: RiskItem, journalDate: string): Finding {
  return {
    location: item.vendorName,
    finding: (item.measure ?? '').trim() || item.hazard,
    action: null,
    responsible: item.owner,
    dueDate: item.dueDate ?? journalDate,
    status: 'OPEN',
    doneDate: null,
  };
}

const sameCheck = (a: Pick<Finding, 'location' | 'finding'>, b: Pick<Finding, 'location' | 'finding'>) =>
  (a.location ?? '') === (b.location ?? '') && a.finding === b.finding;

/**
 * TBM 일지(V572 · DOC-136) — 업체별 하루 한 장의 작업 전 안전점검회의.
 *
 * <p>작업종류는 그 현장 위험성평가의 「공종 - 작업」 에서 고른다. 고르면 그 작업의 중점관리 줄이 체크리스트로 들어오고,
 * 풀면 그 작업에서 들어온 줄 중 아직 미조치인 줄이 빠진다(조치한 줄 · 직접 더한 줄은 남는다).
 * 체크리스트는 지적사항 표를 그대로 쓴다 — location = 업체, finding = 점검내용.
 */
export function TbmSheet({
  journal,
  siteId,
  edit,
  approvals,
  journalId,
}: {
  journal: Omit<JournalInput, 'siteId'>;
  siteId: string | null;
  edit: PaperEdit;
  approvals?: Approvals;
  /** 저장된 일지의 id — 있을 때만 참여자 서명(QR) 칸이 선다. */
  journalId?: string | null;
}) {
  const risk = useSiteRiskItems(siteId);
  const pool = risk.data ?? [];
  const works = [...new Set(pool.map(workLabel).filter(Boolean))];
  const vendors = [...new Set(pool.map((row) => row.vendorName).filter((one): one is string => !!one))];
  const chosen = journal.workItems ?? [];
  const checks = journal.findings;
  const text = (value: string) => (value === '' ? null : value);

  const toggleWork = (work: string) => {
    const keyItems = pool.filter((row) => row.keyPoint && workLabel(row) === work);
    if (chosen.includes(work)) {
      const fromWork = keyItems.map((row) => checkOf(row, journal.journalDate));
      edit({
        workItems: chosen.filter((one) => one !== work),
        findings: checks.filter((row) => row.status !== 'OPEN' || !fromWork.some((one) => sameCheck(one, row))),
      });
      return;
    }
    const added = keyItems.map((row) => checkOf(row, journal.journalDate)).filter((one) => !checks.some((row) => sameCheck(one, row)));
    edit({ workItems: [...chosen, work], findings: [...checks, ...added] });
  };

  const setCheck = (at: number, next: Partial<Finding>) =>
    edit({ findings: checks.map((row, index) => (index === at ? { ...row, ...next } : row)) });
  const today = todayString();
  const openCount = checks.filter((row) => row.status === 'OPEN').length;
  const [caseOpen, setCaseOpen] = useState(false);

  return (
    <div className="ss-sheet">
      <style>{SHEET_STYLES}</style>
      <SheetHead title="TBM 일지" approvals={approvals} />

      <div className="ss-fields">
        <SheetField label="작성업체">
          <input className="input" list="tbm-vendors" value={journal.vendorName ?? ''} onChange={(e) => edit({ vendorName: text(e.target.value) })} />
          <datalist id="tbm-vendors">
            {vendors.map((one) => (
              <option key={one} value={one} />
            ))}
          </datalist>
        </SheetField>
        <SheetField label="장소">
          <input className="input" value={journal.place ?? ''} onChange={(e) => edit({ place: text(e.target.value) })} />
        </SheetField>
        <SheetField label="작업시간">
          <div className="flex items-center gap-2">
            <input type="time" className="input" value={journal.startTime ?? ''} onChange={(e) => edit({ startTime: text(e.target.value) })} />
            <span style={{ color: 'var(--text-muted)' }}>~</span>
            <input type="time" className="input" value={journal.endTime ?? ''} onChange={(e) => edit({ endTime: text(e.target.value) })} />
          </div>
        </SheetField>
        <SheetField label="회의 참석자">
          <input className="input" value={journal.attendeeNames ?? ''} onChange={(e) => edit({ attendeeNames: text(e.target.value) })} />
        </SheetField>
      </div>

      <div className="ss-section">작업종류</div>
      {works.length === 0 && chosen.length === 0 ? (
        <div className="ss-empty">위험성평가에 등록된 작업이 없습니다.</div>
      ) : (
        <div className="ss-chips">
          {[...works, ...chosen.filter((one) => !works.includes(one))].map((work) => (
            <button key={work} type="button" className={`ss-chip ${chosen.includes(work) ? 'on' : ''}`} onClick={() => toggleWork(work)}>
              {chosen.includes(work) ? '✓ ' : ''}
              {work}
            </button>
          ))}
        </div>
      )}

      <div className="ss-section">
        <span>
          위험성평가 체크리스트 <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>· 미조치 {openCount}건</span>
        </span>
        <button
          type="button"
          className="btn-secondary"
          style={{ fontSize: '12px', padding: '4px 10px' }}
          onClick={() =>
            edit({
              findings: [
                ...checks,
                { location: journal.vendorName ?? null, finding: '', action: null, responsible: null, dueDate: journal.journalDate, status: 'OPEN', doneDate: null },
              ],
            })
          }
        >
          + 직접 추가
        </button>
      </div>
      <table className="ss-grid">
        <colgroup>
          <col style={{ width: '34px' }} />
          <col style={{ width: '13%' }} />
          <col />
          <col style={{ width: '12%' }} />
          <col style={{ width: '112px' }} />
          <col style={{ width: '78px' }} />
          <col style={{ width: '112px' }} />
          <col style={{ width: '32px' }} />
        </colgroup>
        <thead>
          <tr>
            <th>No</th>
            <th>업체</th>
            <th>점검내용</th>
            <th>조치담당</th>
            <th>조치예정</th>
            <th>상태</th>
            <th>조치일</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {checks.length === 0 ? (
            <tr>
              <td colSpan={8} className="ss-c ss-empty">
                없음
              </td>
            </tr>
          ) : (
            checks.map((row, at) => (
              <tr key={row.id ?? `new-${at}`}>
                <td className="ss-c">{at + 1}</td>
                <td>
                  <input className="ss-cell" value={row.location ?? ''} onChange={(e) => setCheck(at, { location: text(e.target.value) })} />
                </td>
                <td>
                  <GrowTextarea className="ss-cell" value={row.finding} onChange={(e) => setCheck(at, { finding: e.target.value })} />
                </td>
                <td>
                  <input className="ss-cell" value={row.responsible ?? ''} onChange={(e) => setCheck(at, { responsible: text(e.target.value) })} />
                </td>
                <td>
                  <input type="date" className="ss-cell" value={row.dueDate ?? ''} onChange={(e) => setCheck(at, { dueDate: text(e.target.value) })} />
                </td>
                <td className="ss-c">
                  <button
                    type="button"
                    className={`badge ${row.status === 'DONE' ? 'badge-green' : 'badge-red'}`}
                    style={{ border: 'none', cursor: 'pointer' }}
                    onClick={() =>
                      setCheck(at, row.status === 'DONE' ? { status: 'OPEN', doneDate: null } : { status: 'DONE', doneDate: row.doneDate ?? today })
                    }
                  >
                    {row.status === 'DONE' ? '조치' : '미조치'}
                  </button>
                </td>
                <td>
                  <input
                    type="date"
                    className="ss-cell"
                    value={row.doneDate ?? ''}
                    onChange={(e) => setCheck(at, { doneDate: text(e.target.value), status: e.target.value ? 'DONE' : 'OPEN' })}
                  />
                </td>
                <td className="ss-c">
                  <button type="button" className="ss-x" aria-label="줄 지우기" onClick={() => edit({ findings: checks.filter((_, index) => index !== at) })}>
                    ×
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      <div className="ss-section">
        <span>특이사항 (교육 · 회의 · 행사 등)</span>
        <button type="button" className="btn-secondary" style={{ fontSize: '12px', padding: '4px 10px' }} onClick={() => setCaseOpen(true)}>
          사고사례
        </button>
      </div>
      {/* 1일 1사고사례 — 고른 작업종류의 공종에 맞는 사례를 앞에 보이고, 고르면 특이사항 끝에 한 줄로 붙는다. */}
      {caseOpen && (
        <CaseSuggestPicker works={chosen} onPick={(line) => edit({ content: appendCaseLine(journal.content, line) })} onClose={() => setCaseOpen(false)} />
      )}
      <textarea className="input" rows={3} style={{ width: '100%' }} value={journal.content ?? ''} onChange={(e) => edit({ content: text(e.target.value) })} />

      {journalId && <TbmSignSection journalId={journalId} title={[journal.journalDate, journal.vendorName].filter(Boolean).join(' ')} />}
    </div>
  );
}

const timeOf = (value: string) => new Date(value).toLocaleString('ko-KR', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' });

/** 참여자 서명 — QR 로 받은 근로자 서명 명단(V577). [QR] 은 링크를 여는 창, 줄마다 [×]. */
function TbmSignSection({ journalId, title }: { journalId: string; title: string }) {
  const link = useTbmSignLink(journalId);
  const rows = useTbmSignatures(journalId, !!link.data?.open);
  const remove = useTbmSignatureDelete(journalId);
  const [qrOpen, setQrOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const list = rows.data ?? [];

  return (
    <>
      <div className="ss-section">
        <span>
          참여자 서명 <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>· {list.length}명</span>
        </span>
        <button type="button" className="btn-secondary" style={{ fontSize: '12px', padding: '4px 10px' }} onClick={() => setQrOpen(true)}>
          {link.data?.open && (
            <span
              aria-label="받는 중"
              style={{ display: 'inline-block', width: '6px', height: '6px', borderRadius: '50%', background: '#16a34a', marginRight: '5px', verticalAlign: 'middle' }}
            />
          )}
          QR
        </button>
      </div>
      {error && <Notice tone="warning">{error}</Notice>}
      <table className="ss-grid">
        <colgroup>
          <col style={{ width: '34px' }} />
          <col style={{ width: '22%' }} />
          <col />
          <col style={{ width: '140px' }} />
          <col style={{ width: '112px' }} />
          <col style={{ width: '32px' }} />
        </colgroup>
        <thead>
          <tr>
            <th>No</th>
            <th>성명</th>
            <th>소속</th>
            <th>서명</th>
            <th>시각</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {list.length === 0 ? (
            <tr>
              <td colSpan={6} className="ss-c ss-empty">
                등록된 서명이 없습니다.
              </td>
            </tr>
          ) : (
            list.map((row, at) => (
              <tr key={row.id}>
                <td className="ss-c">{at + 1}</td>
                <td>{row.name}</td>
                <td>{row.affiliation ?? ''}</td>
                <td className="ss-c">{row.signUrl && <img src={row.signUrl} alt="서명" style={{ height: '36px', maxWidth: '100%', objectFit: 'contain' }} />}</td>
                <td className="ss-c">{timeOf(row.signedAt)}</td>
                <td className="ss-c">
                  <button
                    type="button"
                    className="ss-x"
                    aria-label="서명 지우기"
                    onClick={() => {
                      if (!window.confirm(`${row.name} 의 서명을 지울까요?`)) return;
                      setError(null);
                      remove.mutate(row.id, { onError: (cause) => setError(errorMessage(cause)) });
                    }}
                  >
                    ×
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      {qrOpen && <TbmQrModal journalId={journalId} title={title} onClose={() => setQrOpen(false)} />}
    </>
  );
}

/** 참여자 서명 QR 창 — 열면 링크를 연다(없으면 만들고, 있으면 지금부터 24시간 다시). 버튼은 제목 줄. */
function TbmQrModal({ journalId, title, onClose }: { journalId: string; title: string; onClose: () => void }) {
  const link = useTbmSignLink(journalId);
  const write = useTbmSignLinkWrite(journalId);
  const signed = useTbmSignatures(journalId, true);
  const [qr, setQr] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // 창을 열면 링크를 연다 — [QR] 을 누른 것이 곧 「지금 받겠다」 이다.
  useEffect(() => {
    write.mutate('open', { onError: (cause) => setError(errorMessage(cause)) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journalId]);

  const data = write.data ?? link.data ?? null;
  const url = data ? tbmSignUrl(data.token) : null;
  useEffect(() => {
    if (!url) return;
    let alive = true;
    void QRCode.toDataURL(url, { width: 720, margin: 1, errorCorrectionLevel: 'M' }).then((value) => {
      if (alive) setQr(value);
    });
    return () => {
      alive = false;
    };
  }, [url]);

  const run = (action: 'open' | 'close') => write.mutate(action, { onError: (cause) => setError(errorMessage(cause)) });

  return (
    <Modal
      title="참여자 서명 QR"
      onClose={onClose}
      maxWidth="760px"
      actions={
        <div className="flex flex-wrap items-center justify-end gap-2">
          <QrActions
            url={url}
            qr={qr}
            name={`TBM_${title}`}
            poster={{ title: 'TBM 참여자 서명', siteName: title, lines: ['휴대폰 카메라로 QR 을 비춥니다', '오늘의 작업과 체크리스트를 확인합니다', '성명 · 소속을 적고 서명합니다'] }}
          />
          {data?.open ? (
            <button className="btn-secondary" disabled={write.isPending} onClick={() => run('close')}>
              QR 닫기
            </button>
          ) : (
            <button className="btn-primary" disabled={write.isPending} onClick={() => run('open')}>
              다시 열기
            </button>
          )}
        </div>
      }
    >
      <div className="space-y-3" style={{ textAlign: 'center' }}>
        {error && <Notice tone="warning">{error}</Notice>}
        <div style={{ fontWeight: 700 }}>{title}</div>
        <div style={{ position: 'relative', width: '320px', height: '320px', margin: '0 auto' }}>
          {qr && <img src={qr} alt="QR" style={{ width: '320px', height: '320px', imageRendering: 'pixelated', opacity: data?.open ? 1 : 0.25 }} />}
          {data && !data.open && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '18px' }}>
              닫힘
            </div>
          )}
        </div>
        {url && (
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', wordBreak: 'break-all' }}>
            {url}
          </div>
        )}
        <div style={{ fontSize: '13px' }}>
          {data?.open ? `${timeOf(data.expiresAt)} 까지 · ` : ''}서명 <b>{signed.data?.length ?? data?.signed ?? 0}</b>명
        </div>
      </div>
    </Modal>
  );
}
